import React, { useState, useEffect, useContext } from 'react';
import { AuthProvider, AuthContext } from './context/AuthContext';
import Header from './components/Layout/Header';
import FileUploader from './components/Upload/FileUploader';
import DocumentPreview from './components/Preview/DocumentPreview';
import ExtractedData from './components/Results/ExtractedData';
import BookTablesView from './components/BookTables/BookTablesView';
import DocCompareView from './components/DocCompare/DocCompareView';
import { previewDocument, processDocument, saveExtractionAnalysis, getExtractionAnalysis, getExtractionAnalysisPreview } from './services/documents';
import { fetchExtractionConfig } from './services/extractionConfig';
import { exportToExcel } from './services/excel';
import { sendPurchaseInvoice, sendPurchaseInvoiceAttachment } from './services/purchaseInvoice';
import './App.css';

function AppContent() {
  const { userHash } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingProcess, setLoadingProcess] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [images, setImages] = useState([]);
  const [extractionConfig, setExtractionConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [lantagoLoading, setLantagoLoading] = useState(false);
  const [lantagoStatus, setLantagoStatus] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [processingTime, setProcessingTime] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [analysisHash, setAnalysisHash] = useState(null);
  const [documentFilename, setDocumentFilename] = useState(null);
  const [directMode, setDirectMode] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bg = params.get('bg');
    const btn = params.get('btn');
    const hover = params.get('hover');

    if (bg) document.documentElement.style.setProperty('--background', `#${bg}`);
    if (btn) document.documentElement.style.setProperty('--primary-color', `#${btn}`);
    if (hover) document.documentElement.style.setProperty('--primary-hover', `#${hover}`);

    const mode = params.get('mode');
    if (mode === 'book_tables' || mode === 'doc_compare') {
      setDirectMode(mode);
      setLoadingConfig(false);
      return;
    }

    const accessKey = params.get('accessKey');
    if (!accessKey) {
      setConfigError('Uppss!: Se debe suministrar una clave de acceso para acceder a la aplicacion.');
      setLoadingConfig(false);
      return;
    }

    const analysisHashParam = params.get('analysisHash');

    fetchExtractionConfig(accessKey)
      .then(async (config) => {
        setExtractionConfig(config);

        if (analysisHashParam) {
          try {
            const [analysis, preview] = await Promise.all([
              getExtractionAnalysis(analysisHashParam, accessKey),
              getExtractionAnalysisPreview(analysisHashParam, accessKey),
            ]);
            setResult(JSON.parse(analysis.extractionResult));
            setImages(preview.images || []);
            setAnalysisHash(analysisHashParam);
            setDocumentFilename(analysis.documentFilename);
            setSaved(true);
          } catch (err) {
            console.error('Error loading analysis:', err);
            setError('Error al cargar el análisis guardado');
          }
        }
      })
      .catch(() => setConfigError('Uppss!: La clave de acceso suministrada no es valida.'))
      .finally(() => setLoadingConfig(false));
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (result && !saved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [result, saved]);

  const rawFieldsConfig = extractionConfig?.fieldsConfig ? JSON.parse(extractionConfig.fieldsConfig) : null;
  const { export_endpoint: exportEndpoint, ...displayConfig } = rawFieldsConfig || {};
  const fieldsConfig = Object.keys(displayConfig).length > 0 ? displayConfig : null;

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setError(null);
    setResult(null);
    setImages([]);
    setLoadingPreview(true);
    setLoadingProcess(true);
    setLantagoStatus(null);
    setSaved(false);
    setSaveStatus(null);
    setAnalysisHash(null);
    setTokenUsage(null);
    setProcessingTime(null);

    try {
      const previewResponse = await previewDocument(selectedFile);
      setImages(previewResponse.images || []);
      setLoadingPreview(false);

      const processResponse = await processDocument(selectedFile, extractionConfig);
      setResult(processResponse.vlm_analysis);
      setTokenUsage(processResponse.token_usage);
      setProcessingTime(processResponse.processing_time);
    } catch (err) {
      setError(err.message || 'Error procesando el documento');
    } finally {
      setLoadingPreview(false);
      setLoadingProcess(false);
    }
  };

  const handleDataChange = (newData) => {
    setResult(newData);
    if (saved) setSaved(false);
  };

  const handleExport = () => {
    if (!result) return;
    const originalName = (file?.name || documentFilename || 'documento').replace(/\.[^/.]+$/, '');
    const filename = `${originalName}_extraction`;
    exportToExcel(result, filename, fieldsConfig, extractionConfig?.excelTemplateType);
  };

  const handleSave = async () => {
    if (!result) return;
    setSaveLoading(true);
    setSaveStatus(null);

    try {
      const accessKey = new URLSearchParams(window.location.search).get('accessKey');
      const response = await saveExtractionAnalysis(file, accessKey, result, tokenUsage, processingTime, analysisHash, userHash);
      setAnalysisHash(response.hash);
      if (file) setDocumentFilename(file.name);
      setSaved(true);
      setSaveStatus({ type: 'success', message: 'Análisis guardado correctamente' });
    } catch (err) {
      console.error('handleSave - error:', err.response?.data || err.message);
      setSaveStatus({ type: 'error', message: 'Error al guardar el análisis' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSendToLantago = async () => {
    if (!result) return;
    setLantagoLoading(true);
    setLantagoStatus(null);

    try {
      const accessKey = new URLSearchParams(window.location.search).get('accessKey');
      const invoiceResponse = await sendPurchaseInvoice(result, accessKey);

      try {
        await sendPurchaseInvoiceAttachment(file, invoiceResponse.hash, accessKey);
      } catch (attachErr) {
        console.error('handleSendToLantago - attachment error:', attachErr.response?.data || attachErr.message);
        setLantagoStatus({ type: 'error', message: 'Factura creada, pero error al adjuntar el PDF' });
        return;
      }

      setLantagoStatus({ type: 'success', message: 'Factura enviada correctamente' });
    } catch (err) {
      console.error('handleSendToLantago - error:', err.response?.data || err.message);
      setLantagoStatus({ type: 'error', message: 'Error al enviar la factura' });
    } finally {
      setLantagoLoading(false);
    }
  };

  const handleDownloadFile = async () => {
    if (!analysisHash) return;
    try {
      const accessKey = new URLSearchParams(window.location.search).get('accessKey');
      const response = await fetch(`/api/extraction-analysis/${analysisHash}/download?accessKey=${accessKey}`);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentFilename || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('handleDownloadFile - error:', err.message);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setImages([]);
    setError(null);
    setLantagoStatus(null);
    setSaved(false);
    setSaveLoading(false);
    setSaveStatus(null);
    setAnalysisHash(null);
    setTokenUsage(null);
    setProcessingTime(null);
    setDocumentFilename(null);
  };

  if (loadingConfig) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Cargando configuracion...</p>
          </div>
        </main>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="error-container">
            <p>{configError}</p>
          </div>
        </main>
      </div>
    );
  }

  if (directMode === 'book_tables' || extractionConfig?.excelTemplateType === 'book_tables') {
    return (
      <div className="app">
        <Header
          title={extractionConfig?.name || 'Extractor de tablas'}
          subtitle={extractionConfig?.description || 'Extrae todas las tablas de un documento a Excel'}
        />
        <main className="main-content">
          <BookTablesView />
        </main>
      </div>
    );
  }

  if (directMode === 'doc_compare' || extractionConfig?.excelTemplateType === 'doc_compare') {
    return (
      <div className="app">
        <Header
          title={extractionConfig?.name || 'Comparador de documentos'}
          subtitle={extractionConfig?.description || 'Compara dos documentos y detecta cláusulas iguales y distintas'}
        />
        <main className="main-content">
          <DocCompareView />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <Header title={extractionConfig?.name} subtitle={extractionConfig?.description} />
      <main className="main-content">
        {!file && !result ? (
          <FileUploader onFileSelect={handleFileSelect} />
        ) : (
          <div className="document-view">
            <div className="preview-section">
              <DocumentPreview
                images={images}
                loading={loadingPreview}
                metadata={result ? {} : null}
              />
              {analysisHash && (
                <button className="download-button" onClick={handleDownloadFile}>
                  Descargar documento
                </button>
              )}
              <button className="reset-button" onClick={handleReset}>
                Nuevo documento
              </button>
            </div>
            <div className="data-section">
              {loadingProcess && (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Extrayendo datos...</p>
                </div>
              )}
              {error && (
                <div className="error-container">
                  <p>{error}</p>
                </div>
              )}
              {result && !loadingProcess && (
                <>
                  <ExtractedData data={result} onChange={handleDataChange} fieldsConfig={fieldsConfig} />
                  <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={saveLoading || saved}
                  >
                    {saveLoading ? 'Guardando...' : saved ? 'Guardado' : 'Validar y Guardar'}
                  </button>
                  {saveStatus && (
                    <div className={`save-status ${saveStatus.type}`}>
                      {saveStatus.message}
                    </div>
                  )}
                  <button className="export-button" onClick={handleExport}>
                    Exportar a Excel
                  </button>
                  {exportEndpoint && (
                    <>
                      <button
                        className="lantago-button"
                        onClick={handleSendToLantago}
                        disabled={lantagoLoading}
                      >
                        {lantagoLoading ? 'Enviando...' : (exportEndpoint.button_label || 'Enviar a Lantago')}
                      </button>
                      {lantagoStatus && (
                        <div className={`lantago-status ${lantagoStatus.type}`}>
                          {lantagoStatus.message}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
